import React, { useState } from "react";
import { authService } from "../../services/authService";
import { ModalWrapper } from "./ModalWrapper";
import { POPUP_STYLES } from "../../constants/popupStyles";

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      await authService.requestPasswordReset(email);
      setSuccess(true);
      setEmail("");

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to send reset email. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEmail("");
    setError(null);
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleCancel}
      title="Reset Password"
      isLoading={isLoading}
    >
      {success ? (
        <div className="text-center py-5 text-green-800">
          <p className="text-base mb-0 font-semibold">
            ✓ Reset email sent successfully. Please check your inbox.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              placeholder="Email Address"
              className={`${POPUP_STYLES.input} ${error ? 'bg-red-50 border-2 border-red-400' : ''}`}
            />
            {error && (
              <p className="text-red-600 text-xs mt-2 mb-0">
                {error}
              </p>
            )}
          </div>

          <div className="flex gap-4 mt-6">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
              className={`flex-1 ${POPUP_STYLES.button}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`flex-1 ${POPUP_STYLES.button}`}
            >
              {isLoading ? "Sending..." : "OK"}
            </button>
          </div>
        </form>
      )}
    </ModalWrapper>
  );
};
